import React, { useMemo } from 'react';
import './style.css';

const MIN_DIMENSION = 1;
const MIN_STROKE = 1;
const MAX_STROKE = 24;
const DEFAULT_COLOR = '#111827';
const DEFAULT_BACKGROUND = '#ffffff';
const MIN_DIVISIONS = 1;
const MAX_DIVISIONS = 200;
const DEFAULT_TABLE_FONT_SIZE = 10;
const MIN_TABLE_FONT_SIZE = 6;
const MAX_TABLE_FONT_SIZE = 72;

const dashPatternForStyle = (style) => {
  switch ((style || '').toLowerCase()) {
    case 'dashed':
      return '20 12';
    case 'dotted':
    case 'pointed':
      return '2 10';
    case 'dashdot':
    case 'dash-dot':
    case 'dash-point':
      return '22 10 2 10';
    default:
      return null;
  }
};

const sanitizeDimension = (value, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return Math.max(MIN_DIMENSION, Number(fallback) || MIN_DIMENSION);
  }
  return Math.max(MIN_DIMENSION, numeric);
};

const sanitizeStrokeWidth = (value, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return Math.min(MAX_STROKE, Math.max(MIN_STROKE, Number(fallback) || MIN_STROKE));
  }
  return Math.min(MAX_STROKE, Math.max(MIN_STROKE, numeric));
};

const sanitizeAlpha = (value, fallback = 1) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.min(1, Math.max(0, numeric));
};

const sanitizeDivisionCount = (value, fallback = 1) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.min(MAX_DIVISIONS, Math.max(MIN_DIVISIONS, Math.round(numeric)));
};

const sanitizeTableFontSize = (value, fallback = DEFAULT_TABLE_FONT_SIZE) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.min(MAX_TABLE_FONT_SIZE, Math.max(MIN_TABLE_FONT_SIZE, Math.round(numeric)));
};

const BoxNode = ({ data, selected }) => {
  const attributes = data?.attributes || {};
  const strokeWidthAttr = attributes.strokeWidth;
  const widthAttr = attributes.width;
  const heightAttr = attributes.height;
  const borderStyleAttr = attributes.borderStyle;
  const backgroundAlphaAttr = attributes.backgroundAlpha;
  const strokeAlphaAttr = attributes.strokeAlpha;
  const columnsAttr = attributes.columns;
  const rowsAttr = attributes.rows;
  const tableFontSizeAttr = attributes.tableFontSize;
  const backgroundColor = useMemo(() => {
    const attrColor = typeof attributes?.backgroundColor?.value === 'string'
      ? attributes.backgroundColor.value.trim()
      : '';
    return attrColor && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(attrColor)
      ? attrColor
      : DEFAULT_BACKGROUND;
  }, [attributes?.backgroundColor?.value]);
  const strokeColor = useMemo(() => {
    const rawTint = typeof data?.tintColor === 'string' ? data.tintColor.trim() : '';
    const attrColor = typeof attributes?.strokeColor?.value === 'string'
      ? attributes.strokeColor.value.trim()
      : '';
    const pick = attrColor || rawTint;
    return pick && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(pick) ? pick : DEFAULT_COLOR;
  }, [data?.tintColor, attributes?.strokeColor?.value]);
  const backgroundAlpha = useMemo(
    () => sanitizeAlpha(backgroundAlphaAttr?.value, 1),
    [backgroundAlphaAttr?.value],
  );
  const strokeAlpha = useMemo(
    () => sanitizeAlpha(strokeAlphaAttr?.value, 1),
    [strokeAlphaAttr?.value],
  );

  const strokeWidth = useMemo(
    () => sanitizeStrokeWidth(strokeWidthAttr?.value, 3),
    [strokeWidthAttr?.value],
  );
  const columns = useMemo(
    () => sanitizeDivisionCount(columnsAttr?.value, 1),
    [columnsAttr?.value],
  );
  const rows = useMemo(
    () => sanitizeDivisionCount(rowsAttr?.value, 1),
    [rowsAttr?.value],
  );
  const width = useMemo(
    () => sanitizeDimension(widthAttr?.value, 240),
    [widthAttr?.value],
  );
  const height = useMemo(
    () => sanitizeDimension(heightAttr?.value, 160),
    [heightAttr?.value],
  );
  const borderStyle = useMemo(() => {
    const raw = typeof borderStyleAttr?.value === 'string' ? borderStyleAttr.value : 'solid';
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'dotted' || normalized === 'pointed') return 'dotted';
    if (normalized === 'dashdot' || normalized === 'dash-dot' || normalized === 'dashpoint') return 'dashdot';
    if (normalized === 'dashed') return 'dashed';
    return 'solid';
  }, [borderStyleAttr?.value]);
  const dashPattern = useMemo(() => dashPatternForStyle(borderStyle), [borderStyle]);
  const divisionStrokeWidth = Math.max(1, Math.min(strokeWidth, 3));
  const table = useMemo(() => {
    const rawTable = data?.table;
    const headers = Array.isArray(rawTable?.headers) ? rawTable.headers : [];
    const rows = Array.isArray(rawTable?.rows) ? rawTable.rows : [];
    if (!headers.length) {
      return null;
    }
    return {
      headers: headers.map((header) => String(header ?? '')),
      rows: rows.map((row) =>
        Array.isArray(row)
          ? headers.map((_, index) => String(row[index] ?? ''))
          : headers.map(() => '')
      ),
    };
  }, [data?.table]);
  const tableRowCount = table ? Math.max(1, table.rows.length + 1) : rows;
  const tableColumnCount = table ? Math.max(1, table.headers.length) : columns;
  const tableFontSize = useMemo(
    () => sanitizeTableFontSize(tableFontSizeAttr?.value, DEFAULT_TABLE_FONT_SIZE),
    [tableFontSizeAttr?.value],
  );

  const svgMetrics = useMemo(() => {
    const inset = strokeWidth / 2;
    return {
      width: Math.max(MIN_DIMENSION, width),
      height: Math.max(MIN_DIMENSION, height),
      innerWidth: Math.max(0, width - strokeWidth),
      innerHeight: Math.max(0, height - strokeWidth),
      inset,
    };
  }, [width, height, strokeWidth]);

  const nodeClassName = `box-node${table ? ' box-node--table' : ''}${selected ? ' is-selected' : ''}`;

  return (
    <div
      className={nodeClassName}
      style={{ width: `${svgMetrics.width}px`, height: `${svgMetrics.height}px` }}
      data-inline-width={svgMetrics.width}
      data-inline-height={svgMetrics.height}
    >
      <svg
        className="box-node__svg"
        width={svgMetrics.width}
        height={svgMetrics.height}
        role="presentation"
      >
        <rect
          className="box-node__rect"
          x={svgMetrics.inset}
          y={svgMetrics.inset}
          width={Math.max(0, svgMetrics.innerWidth)}
          height={Math.max(0, svgMetrics.innerHeight)}
          stroke={strokeColor}
          strokeOpacity={strokeAlpha}
          strokeWidth={strokeWidth}
          strokeDasharray={dashPattern || undefined}
          strokeLinecap={borderStyle === 'dotted' ? 'round' : 'butt'}
          strokeLinejoin="miter"
          fill={backgroundColor}
          fillOpacity={backgroundAlpha}
          vectorEffect="non-scaling-stroke"
          pointerEvents="all"
        />
        {tableColumnCount > 1
          ? Array.from({ length: tableColumnCount - 1 }, (_, index) => {
              const x = svgMetrics.inset + ((index + 1) * svgMetrics.innerWidth) / tableColumnCount;
              return (
                <line
                  key={`col-${index}`}
                  x1={x}
                  y1={svgMetrics.inset}
                  x2={x}
                  y2={svgMetrics.inset + svgMetrics.innerHeight}
                  stroke={strokeColor}
                  strokeOpacity={strokeAlpha}
                  strokeWidth={divisionStrokeWidth}
                  strokeDasharray={dashPattern || undefined}
                  strokeLinecap={borderStyle === 'dotted' ? 'round' : 'butt'}
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              );
            })
          : null}
        {tableRowCount > 1
          ? Array.from({ length: tableRowCount - 1 }, (_, index) => {
              const y = svgMetrics.inset + ((index + 1) * svgMetrics.innerHeight) / tableRowCount;
              return (
                <line
                  key={`row-${index}`}
                  x1={svgMetrics.inset}
                  y1={y}
                  x2={svgMetrics.inset + svgMetrics.innerWidth}
                  y2={y}
                  stroke={strokeColor}
                  strokeOpacity={strokeAlpha}
                  strokeWidth={divisionStrokeWidth}
                  strokeDasharray={dashPattern || undefined}
                  strokeLinecap={borderStyle === 'dotted' ? 'round' : 'butt'}
                  vectorEffect="non-scaling-stroke"
                  pointerEvents="none"
                />
              );
            })
          : null}
      </svg>
      {table ? (
        <div
          className="box-node__table"
          style={{
            gridTemplateColumns: `repeat(${table.headers.length}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${table.rows.length + 1}, minmax(0, 1fr))`,
            left: `${svgMetrics.inset}px`,
            top: `${svgMetrics.inset}px`,
            width: `${svgMetrics.innerWidth}px`,
            height: `${svgMetrics.innerHeight}px`,
            fontSize: `${tableFontSize}px`,
          }}
        >
          {table.headers.map((header, index) => (
            <div key={`header-${index}`} className="box-node__table-cell box-node__table-cell--header">
              {header}
            </div>
          ))}
          {table.rows.flatMap((row, rowIndex) =>
            table.headers.map((_, columnIndex) => (
              <div
                key={`cell-${rowIndex}-${columnIndex}`}
                className="box-node__table-cell"
              >
                {row[columnIndex]}
              </div>
            ))
          )}
        </div>
      ) : null}
      {data?.showResizeHandle ? (
        <button
          type="button"
          className="box-node__resize"
          aria-label="Redimensionar caja"
          onPointerDown={data.onResizePointerDown}
        />
      ) : null}
    </div>
  );
};

export default React.memo(BoxNode);
